import {
  Component,
  OnDestroy,
  OnInit,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import {
  DrawerType,
  AppointmentStatus,
  IAppointment,
  ILead,
  INote,
  ICustomerUser,
  IAdminTokenPayload
} from '@appgen-health-crm/api-interfaces';
import { State as ChatState } from '../../+store/chat/chat.reducer';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { DrawerStateFacade } from '../../+store/drawer-state/drawer-state.facade';
import { RouterModule } from '@angular/router';
import { ChatFacade } from '../../+store/chat/chat.facade';
import { CustomerFacade } from '../../+store/customer/customer.facade';
import { NoteFacade } from '../../+store/note/note.facade';
import { AppointmentFacade } from '../../+store/appointment/appointment.facade';
import { isAppointmentActive } from '@care-portals/shared';
import { format } from 'date-fns/esm';
import { take, takeWhile } from 'rxjs/operators';
import { AuthFacade } from '../../+store/auth/auth.facade';
import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { CommonModule } from '@angular/common';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { FormsModule } from '@angular/forms';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NameAvatarComponent } from '../notes/widgets/note-avatar/note-avatar.component';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { OverlayService } from '../../services/overlay/overlay.service';
import { ElapsedTimePipe } from '../../pipes/elapsed-time.pipe';

@Component({
  standalone: true,
  imports: [
    NzButtonModule,
    CommonModule,
    NzDividerModule,
    FormsModule,
    NzInputModule,
    NameAvatarComponent,
    NzDropDownModule,
    NzIconModule,
    RouterModule,
    ElapsedTimePipe
  ],
  selector: 'app-session-overlay',
  templateUrl: './session-overlay.component.html',
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({
          height: '40px'
        })
      ),
      state(
        'expanded',
        style({
          height: '680px'
        })
      ),
      state(
        'fullscreen',
        style({
          height: '100vh'
        })
      ),
      transition('collapsed <=> expanded', [animate('300ms ease-in-out')]),
      transition('collapsed <=> fullscreen', [animate('300ms ease-in-out')]),
      transition('fullscreen <=> expanded', [animate('300ms ease-in-out')])
    ])
  ]
})
export class SessionOverlayComponent implements OnInit, OnDestroy, OnChanges {
  appointment: IAppointment;
  lead?: ILead;

  mode: 'fullscreen' | 'expanded' | 'collapsed' = 'expanded';
  position: 'left' | 'right' | 'center' = 'center';

  clinicalNotes = '';
  clinicalNoteId: string | undefined = undefined;
  adminNotes = '';

  /**
   * Chat session this overlay opened, if any. Teardown is scoped to it so
   * ending a session never closes a chat that belongs to another view.
   */
  private chatSessionId: number = null;

  user: IAdminTokenPayload;
  chatState: ChatState;
  isActive = true;

  constructor(
    private readonly appointmentFacade: AppointmentFacade,
    private readonly chatFacade: ChatFacade,
    public readonly customerFacade: CustomerFacade,
    private readonly drawerFacade: DrawerStateFacade,
    private readonly notificationService: NzNotificationService,
    private noteFacade: NoteFacade,
    private authFacade: AuthFacade,
    private readonly overlayService: OverlayService
  ) {
    // Add window beforeunload event listener
    window.addEventListener('beforeunload', event => {
      if (this.appointment?.status === 'in-progress') {
        event.preventDefault();
        event.returnValue =
          'You have an active session. Are you sure you want to leave? Any unsaved changes will be lost.';
        return event.returnValue;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['appointment'] && this.appointment?._id) {
      this.noteFacade.getAppointmentNote(this.appointment._id);
    }
  }

  ngOnInit(): void {
    this.authFacade.user$.subscribe(user => {
      this.user = user;
    });

    this.chatFacade.chatState$
      .pipe(takeWhile(() => this.isActive))
      .subscribe(chatState => {
        this.chatState = chatState;
      });

    this.noteFacade.appointmentNotes$
      .pipe(takeWhile(() => this.isActive))
      .subscribe(res => {
        const notesArr = Object.values(res);

        if (notesArr.length > 0) {
          // appointment can only have one note per session
          // sort notes to get the latest one, we had a bug that allowed saving multiple notes to the same appointment
          // we pick the latest note and ignore the others.
          notesArr.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          );

          this.clinicalNotes = notesArr[0].content;
          this.clinicalNoteId = notesArr[0]._id;
        }
      });

    this.appointmentFacade.selectedAppointment$.subscribe(res => {
      if (!res) return;
      this.appointment = res;
      this.lead = this.appointment.relatedToLead as ILead;
    });
  }

  ngOnDestroy(): void {
    this.isActive = false;
    window.removeEventListener('beforeunload', () => {});
    this.appointmentFacade.clearAppointment();
    this.noteFacade.clearAppointmentNotes();
    // Only tear down the chat we opened ourselves. This used to close
    // unconditionally, which wiped a chat opened elsewhere (e.g. a customer
    // chat drawer) and left it showing "There was an error Loading Customer".
    if (this.chatSessionId != null) {
      this.chatFacade.closeChat(this.chatSessionId);
    }
  }

  finalizeSession(id: string, status: AppointmentStatus) {
    this.appointmentFacade.updateAppointment(id, {
      status,
      notes: this.adminNotes
    });

    this.saveClinicalNote();
  }

  pullAppointment() {
    this.appointmentFacade.pullAppointment();
  }

  openCreateCustomerDrawer() {
    if (this.lead.isPatient) {
      console.error("customer exist; can't create new one");
      return;
    }
    this.drawerFacade.openDrawer({
      name: DrawerType.CREATE_PATIENT,
      props: { appointment: this.appointment }
    });
  }

  startSession() {
    if (
      !isAppointmentActive(this.appointment) ||
      this.appointment.status === 'in-progress'
    ) {
      this.notificationService.warning('Uh oh!', 'Session already started.');
      return;
    }
    if (!this.lead.isPatient) {
      this.notificationService.warning(
        'Uh oh!',
        'A client account must exist to start a session'
      );
      return;
    }
    this.appointmentFacade.updateAppointment(this.appointment._id, {
      status: 'in-progress'
    });
  }

  openCustomer(id: string) {
    this.customerFacade.openCustomer(id);
  }

  isAppointmentActive = () => isAppointmentActive(this.appointment);

  openChat() {
    if (!this.appointment) return;
    const customer = this.lead as unknown as ICustomerUser;
    // An appointment whose lead was never populated would otherwise open a
    // chat with no customer, rendering the "error loading customer" body.
    if (!this.lead?.isPatient || !customer?._id) {
      this.notificationService.error(
        'Chat',
        'This appointment has no patient record.'
      );
      return;
    }
    this.chatFacade.openChat(customer, 'chat');
    this.chatFacade.sessionId$
      .pipe(take(1))
      .subscribe(sessionId => (this.chatSessionId = sessionId));
  }

  saveClinicalNote() {
    if (!this.clinicalNotes?.trim()) {
      return;
    }

    const noteDTO: Partial<INote> = {
      _id: this.clinicalNoteId,
      customer: this.lead._id,
      consultation: undefined,
      appointment: this.appointment._id,
      content: this.clinicalNotes,
      consultType:
        'Session(' +
        format(new Date(this.appointment.reservation), 'yyyy-MM-dd') +
        ')'
    };
    this.noteFacade.upsertNote(noteDTO, true);
  }

  closeSession() {
    this.overlayService.close();
  }
}
